import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { AlertCircle as IconAlertCircle, UserPlus, Users } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import {
  createExamination,
  type DoctorSpecialty,
  type ExaminationCreatePayload,
  type ExaminationType,
} from "@/lib/api/examinations";
import { createPatient, getPatient, listPatients } from "@/lib/api/patients";
import type { Sex } from "@/lib/api/patients";

const TYPES: ExaminationType[] = ["initial", "periodic", "form_086", "form_083", "form_089"];

const SPECIALTIES: DoctorSpecialty[] = [
  "therapist",
  "neurologist",
  "ophthalmologist",
  "ent",
  "surgeon",
  "gynecologist",
  "urologist",
  "dentist",
  "psychiatrist_narcologist",
  "dermatovenerologist",
  "cardiologist",
  "radiologist",
  "laboratory",
];

// Sex-restricted specialties.
const FEMALE_ONLY: DoctorSpecialty[] = ["gynecologist"];
const MALE_ONLY: DoctorSpecialty[] = ["urologist"];

function filterSpecialtiesBySex(sex: Sex | "" | null | undefined): DoctorSpecialty[] {
  if (sex === "female") {
    return SPECIALTIES.filter((s) => !MALE_ONLY.includes(s));
  }
  if (sex === "male") {
    return SPECIALTIES.filter((s) => !FEMALE_ONLY.includes(s));
  }
  return SPECIALTIES;
}

export function OpenExamDialog({
  patientId,
  opened,
  onClose,
}: {
  patientId?: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lockedToPatient = !!patientId;
  const [mode, setMode] = useState<string | null>("existing");
  const [patientSearch, setPatientSearch] = useState("");
  const [debSearch] = useDebouncedValue(patientSearch, 300);

  const form = useForm({
    initialValues: {
      // existing patient mode
      patient_id: patientId ?? "",
      // new patient mode
      family_name: "",
      given_name: "",
      father_name: "",
      birth_date: "",
      sex: "" as Sex | "",
      phone_primary: "",
      // common
      type: "form_086" as ExaminationType,
      job_title: "",
      routing: [] as DoctorSpecialty[],
      ticket_number: "",
    },
    validate: {
      job_title: (v) => (v.trim().length > 0 ? null : t("validation.required")),
      patient_id: (v) =>
        mode === "existing" && !lockedToPatient && !v ? t("validation.required") : null,
      family_name: (v) =>
        mode === "new" && v.trim().length === 0 ? t("validation.required") : null,
      given_name: (v) =>
        mode === "new" && v.trim().length === 0 ? t("validation.required") : null,
      birth_date: (v) =>
        mode === "new" && !v ? t("validation.required") : null,
      sex: (v) => (mode === "new" && !v ? t("validation.required") : null),
      phone_primary: (v) =>
        mode === "new" && v.trim().length < 9 ? t("validation.required") : null,
    },
  });

  const patientsQuery = useQuery({
    queryKey: ["patients-search", debSearch],
    queryFn: () => listPatients({ q: debSearch || undefined, limit: 20 }),
    enabled: mode === "existing" && !lockedToPatient && opened,
  });

  // When in existing-patient mode, fetch the picked patient's full details (for sex filtering)
  const pickedPatientId = lockedToPatient
    ? patientId
    : mode === "existing"
      ? form.values.patient_id
      : "";
  const pickedPatientQuery = useQuery({
    queryKey: ["patient", pickedPatientId],
    queryFn: () => getPatient(pickedPatientId!),
    enabled: !!pickedPatientId && opened,
    staleTime: 60_000,
  });

  const effectiveSex: Sex | "" =
    mode === "new"
      ? (form.values.sex as Sex | "")
      : (pickedPatientQuery.data?.sex ?? "");
  const availableSpecialties = filterSpecialtiesBySex(effectiveSex);

  // Drop any selected routing entries that are no longer valid for the patient's sex.
  useEffect(() => {
    const allowed = new Set(availableSpecialties);
    const cleaned = form.values.routing.filter((s) => allowed.has(s));
    if (cleaned.length !== form.values.routing.length) {
      form.setFieldValue("routing", cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSex]);

  const mutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      let pid = patientId ?? values.patient_id;

      // Create patient first if in "new" mode
      if (!lockedToPatient && mode === "new") {
        const newPatient = await createPatient({
          family_name: values.family_name.trim(),
          given_name: values.given_name.trim(),
          father_name: values.father_name.trim() || null,
          birth_date: values.birth_date,
          sex: values.sex as Sex,
          phone_primary: values.phone_primary.trim(),
          consent_signed: false,
        });
        pid = newPatient.id;
      }

      if (!pid) throw new Error("Patient required");

      const payload: ExaminationCreatePayload = {
        patient_id: pid,
        type: values.type,
        job_title: values.job_title.trim(),
        routing: values.routing.length > 0 ? values.routing : null,
        ticket_number: values.ticket_number || null,
      };
      return createExamination(payload);
    },
    onSuccess: (exam) => {
      queryClient.invalidateQueries({ queryKey: ["examinations"] });
      queryClient.invalidateQueries({ queryKey: ["profpatolog-queue"] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      notifications.show({ color: "green", message: t("examinations.created") });
      form.reset();
      onClose();
      navigate(`/examinations/${exam.id}`);
    },
  });

  const onSubmit = form.onSubmit((values) => {
    mutation.mutate(values);
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={t("examinations.open_title")}
      size="lg"
      radius="lg"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md" radius="lg">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={onSubmit}>
        <Stack>
          {!lockedToPatient && (
            <Tabs value={mode} onChange={setMode}>
              <Tabs.List>
                <Tabs.Tab
                  value="existing"
                  leftSection={<Users size={16} strokeWidth={2} />}
                >
                  {t("examinations.tab_existing")}
                </Tabs.Tab>
                <Tabs.Tab
                  value="new"
                  leftSection={<UserPlus size={16} strokeWidth={2} />}
                >
                  {t("examinations.tab_new_patient")}
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="existing" pt="md">
                <Select
                  label={t("examinations.fields.patient")}
                  placeholder={t("examinations.search_patient")}
                  required
                  searchable
                  data={(patientsQuery.data?.items ?? []).map((p) => ({
                    value: p.id,
                    label: `${p.family_name} ${p.given_name} · ${p.card_number}`,
                  }))}
                  searchValue={patientSearch}
                  onSearchChange={setPatientSearch}
                  nothingFoundMessage={t("examinations.no_patient_found")}
                  {...form.getInputProps("patient_id")}
                />
              </Tabs.Panel>

              <Tabs.Panel value="new" pt="md">
                <Stack>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <TextInput
                      label={t("placeholder.family_name")}
                      placeholder="Rahmonov"
                      required
                      {...form.getInputProps("family_name")}
                    />
                    <TextInput
                      label={t("placeholder.given_name")}
                      placeholder="Bekzod"
                      required
                      {...form.getInputProps("given_name")}
                    />
                  </SimpleGrid>
                  <TextInput
                    label={t("patients.fields.father_name")}
                    placeholder="Akbarovich"
                    {...form.getInputProps("father_name")}
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <TextInput
                      label={t("patients.fields.birth_date")}
                      type="date"
                      required
                      {...form.getInputProps("birth_date")}
                    />
                    <Select
                      label={t("patients.fields.sex")}
                      placeholder={t("placeholder.select_sex")}
                      required
                      data={[
                        { value: "male", label: t("patients.sex.male") },
                        { value: "female", label: t("patients.sex.female") },
                      ]}
                      {...form.getInputProps("sex")}
                    />
                  </SimpleGrid>
                  <TextInput
                    label={t("patients.fields.phone")}
                    placeholder={t("placeholder.phone")}
                    required
                    {...form.getInputProps("phone_primary")}
                  />
                </Stack>
              </Tabs.Panel>
            </Tabs>
          )}

          <TextInput
            label={t("examinations.fields.job_title")}
            placeholder={t("examinations.job_title_placeholder")}
            required
            {...form.getInputProps("job_title")}
          />
          <Select
            label={t("examinations.fields.type")}
            required
            data={TYPES.map((tp) => ({ value: tp, label: t(`examinations.types.${tp}`) }))}
            {...form.getInputProps("type")}
          />
          <MultiSelect
            label={t("examinations.fields.routing")}
            description={
              effectiveSex === "male"
                ? t("examinations.routing_hint_male")
                : t("examinations.routing_hint")
            }
            searchable
            data={availableSpecialties.map((s) => ({
              value: s,
              label: t(`users.specialties.${s}`),
            }))}
            {...form.getInputProps("routing")}
          />
          <TextInput
            label={t("examinations.fields.ticket")}
            placeholder="123"
            {...form.getInputProps("ticket_number")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {t("examinations.open_action")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
